import {
    mdiAccountCircle,
    mdiAccountMultiple,
    mdiCheckBold,
    mdiChevronDown,
    mdiChevronLeft,
    mdiChevronRight,
    mdiChevronUp,
    mdiExclamationThick,
    mdiOpenInNew,
    mdiProgressHelper,
    mdiUnfoldLessVertical,
    mdiUnfoldMoreVertical,
} from '@mdi/js';
import * as React from 'react';
import { useState } from 'react';
import * as ReactDOM from 'react-dom';

import { CriteriaIdType } from '../performance-data/metric.pd.model';

import { Context, RenderContext, SortOrder } from './experimental.grid';
import {
    CellContext,
    ColumnContext,
    ColumnType,
    MetricPair,
    NodeInfo,
    RowContext,
} from './grid.insight.model';
import { TranslationKeyMap } from './insight.i18n';
import Tooltip from './tooltip.component';

// TODO replace with JedWatson/classnames?
export function classNames(items: { [key: string]: any } | string): string {
    return Object.keys(items)
        .reduce((p, c) => {
            const value = items[c];
            if (value === true) {
                p.push(c);
            }
            return p;
        }, [])
        .join(' ');
}

function DefaultGrid(props: Context): JSX.Element {
    const [renderTrigger, setRenderTrigger] = useState(false);
    (props as any).setRenderTrigger(renderTrigger, setRenderTrigger);

    return <></>;
}

function DefaultRow(props: Context): JSX.Element {
    const [renderTrigger, setRenderTrigger] = useState(false);
    (props as any).setRenderTrigger(renderTrigger, setRenderTrigger);

    return <div className="insight-default" />;
}

function DefaultCell(props: Context): JSX.Element {
    const [renderTrigger, setRenderTrigger] = useState(false);
    (props as any).setRenderTrigger(renderTrigger, setRenderTrigger);

    return <div className="insight-default">{props.ctx.value}</div>;
}

const ExpandPillar = <T extends { expanded: boolean; onClick: () => void }>(
    props: T,
): JSX.Element => {
    return (
        <div className="expand-pillar-wrapper">
            <button
                type="button"
                className="btn bg-transparent p-0"
                tabIndex={-1}
                onClick={props.onClick}
            >
                <div className="d-inline cmps-icon cmps-cdk-fill-grey-100">
                    <svg>
                        {!props.expanded && <path d={mdiChevronLeft}></path>}
                        {props.expanded && <path d={mdiChevronUp}></path>}
                    </svg>
                </div>
            </button>
        </div>
    );
};

const ExpandMetric = <T extends { expanded: boolean; onClick: () => void }>(
    props: T,
): JSX.Element => {
    return (
        <div className="expand-metric-wrapper">
            <button
                type="button"
                className="btn bg-transparent p-0"
                tabIndex={-1}
                onClick={props.onClick}
            >
                <div className="d-inline cmps-icon cmps-cdk-fill-grey-100">
                    <svg>
                        {!props.expanded && (
                            <path d={mdiUnfoldMoreVertical}></path>
                        )}
                        {props.expanded && (
                            <path d={mdiUnfoldLessVertical}></path>
                        )}
                    </svg>
                </div>
            </button>
        </div>
    );
};

export function ColumnElement(
    props: Context<ColumnContext, RowContext, ColumnContext>,
): JSX.Element {
    const [renderTrigger, setRenderTrigger] = useState(false);
    (props as any).setRenderTrigger(renderTrigger, setRenderTrigger);

    const { g, c, l, ctx } = props;
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    const isSortColumn =
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        g.context.ctx.sort &&
        g.context.ctx.sort.def &&
        g.context.ctx.sort.def.columnId === c.id;
    if (ctx.type === ColumnType.PILLAR) {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        l.renderContext &&
            l.renderContext.element.classList.add('insight-pillar-bg');
    }
    if (ctx.type === ColumnType.SUB_PILLAR) {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain, @typescript-eslint/prefer-includes
        l.renderContext &&
            l.renderContext.element.classList.add(
                // eslint-disable-next-line @typescript-eslint/prefer-includes
                c.id.indexOf('-STUB-') !== -1
                    ? 'insight-pillar-bg'
                    : 'insight-sub-pillar-bg',
            );
    }
    if (ctx.type === ColumnType.METRIC) {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        l.renderContext &&
            l.renderContext.element.classList.add('insight-metric-bg');
    }
    if (ctx.type === ColumnType.COMPONENT_METRIC) {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        l.renderContext &&
            l.renderContext.element.classList.add(
                'insight-component-metric-bg',
            );
    }
    return (
        <>
            {ctx.type === ColumnType.PILLAR && (
                <div className="insight-column insight-pillar">
                    <span className="label">{ctx.name}</span>
                </div>
            )}
            {ctx.type === ColumnType.SUB_PILLAR && (
                <div className="insight-column insight-sub-pillar">
                    <ExpandPillar
                        expanded={c.ctx.pillarExpanded}
                        onClick={() =>
                            g.context.ctx.refs.onExpandPillarClick(c)
                        }
                    />
                    <div className="label-wrapper">
                        <span className="label">{ctx.name}</span>
                    </div>
                </div>
            )}
            {ctx.type === ColumnType.METRIC && (
                <div className="insight-column insight-metric">
                    {ctx.hasPillarHandle && (
                        <ExpandPillar
                            expanded={c.ctx.pillarExpanded}
                            onClick={() =>
                                g.context.ctx.refs.onExpandPillarClick(c)
                            }
                        />
                    )}
                    <div className="label-wrapper">
                        <span
                            className="label"
                            data-metric=""
                        >
                            {ctx.name}
                        </span>
                    </div>
                    {ctx.hasComponents && (
                        <ExpandMetric
                            expanded={c.ctx.metricExpanded}
                            onClick={() =>
                                g.context.ctx.refs.onExpandMetricClick(c)
                            }
                        />
                    )}
                </div>
            )}
            {ctx.type === ColumnType.COMPONENT_METRIC && (
                <div className="insight-column insight-component-metric">
                    <span
                        className="label"
                        data-component-metric=""
                    >
                        {ctx.name}
                    </span>
                </div>
            )}
            {/* eslint-disable-next-line @typescript-eslint/prefer-includes */}
            {[
                ColumnType.NODE,
                ColumnType.PILLAR,
                ColumnType.SUB_PILLAR,
                ColumnType.METRIC,
                ColumnType.COMPONENT_METRIC,
            ].indexOf(ctx.type) === -1 && (
                <div className="insight-column insight-optional-column">
                    <button
                        type="button"
                        className="btn bg-transparent p-0 cmps-cdk-d-flex cmps-cdk-align-items-center"
                        tabIndex={-1}
                        onClick={(e) => {
                            e.stopPropagation();
                            g.context.ctx.refs.onSortClick(c);
                        }}
                        data-optional-column=""
                    >
                        <span>{ctx.name}</span>
                        {isSortColumn && (
                            <div className="d-inline cmps-icon cmps-cdk-fill-grey-300">
                                <svg>
                                    {isSortColumn &&
                                        g.context.ctx.sort.def.order ===
                                            SortOrder.DESC && (
                                            <path d={mdiChevronDown}></path>
                                        )}
                                    {isSortColumn &&
                                        g.context.ctx.sort.def.order ===
                                            SortOrder.ASC && (
                                            <path d={mdiChevronUp}></path>
                                        )}
                                </svg>
                            </div>
                        )}
                    </button>
                </div>
            )}
        </>
    );
}

export function NodeElement(
    props: Context<ColumnContext, RowContext, CellContext<NodeInfo>>,
): JSX.Element {
    const [renderTrigger, setRenderTrigger] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);
    const targetRef = React.useRef<HTMLDivElement>(null);
    (props as any).setRenderTrigger(renderTrigger, setRenderTrigger);

    const { g, r, ctx } = props;
    const display = r.context.ctx.levelDataMap.displayNameCriteriaIdType;
    // eslint-disable-next-line @typescript-eslint/prefer-includes
    const hasIcon =
        // eslint-disable-next-line @typescript-eslint/prefer-includes
        [
            CriteriaIdType.USER_ID,
            CriteriaIdType.TEAM_ID,
            CriteriaIdType.SUPER_GROUP_ID,
        ].indexOf(display) !== -1;
    return (
        <div className="w-100">
            {' '}
            {/* for ie11 */}
            <div
                className={`insight-node ${classNames({
                    'insight-has-next-level': r.context.ctx.hasNextLevel,
                })}`}
                style={{
                    paddingRight: '0.5rem',
                    paddingLeft: `calc(0.5rem + (1.8rem * ${r.context.ctx.depth}))`,
                }}
                onClick={() =>
                    r.context.ctx.hasNextLevel &&
                    g.context.ctx.refs.onExpandRowClick(r)
                }
            >
                {r.context.ctx.hasNextLevel && (
                    <button
                        type="button"
                        className="btn bg-transparent p-0 mr-2"
                        tabIndex={-1}
                    >
                        <div className="d-inline cmps-icon cmps-cdk-fill-grey-300">
                            <svg>
                                {r.context.ctx.pending && (
                                    <path d={mdiProgressHelper}></path>
                                )}
                                {!r.context.ctx.pending &&
                                    r.context.ctx.expanded && (
                                        <path d={mdiChevronDown}></path>
                                    )}
                                {!r.context.ctx.pending &&
                                    !r.context.ctx.expanded && (
                                        <path d={mdiChevronRight}></path>
                                    )}
                            </svg>
                        </div>
                    </button>
                )}
                {!r.context.ctx.hasNextLevel && (
                    <div
                        className="btn bg-transparent p-0 mr-2 invisible"
                        aria-hidden={true}
                    >
                        <div className="insight-handle" />
                    </div>
                )}
                {g.context.ctx && hasIcon && (
                    <>
                        {(display === CriteriaIdType.USER_ID ||
                            display === CriteriaIdType.TEAM_ID) && (
                            <div className="d-inline cmps-icon cmps-cdk-fill-grey-300 cmps-cdk-mr-0dot5">
                                <svg>
                                    <path d={mdiAccountCircle}></path>
                                </svg>
                            </div>
                        )}
                        {display === CriteriaIdType.SUPER_GROUP_ID && (
                            <div className="d-inline cmps-icon legacy:cmps-cdk-fill-green-100 cmps-cdk-mr-0dot5">
                                <svg>
                                    <path d={mdiAccountMultiple}></path>
                                </svg>
                            </div>
                        )}
                    </>
                )}
                <span className="mr-auto">
                    {ctx.value.name &&
                        g.context.ctx.refs.translate.instant(ctx.value.name)}
                </span>
                {r.context.ctx.depth !== 0 /* not root node */ &&
                    ctx.value.notCurrentManager && (
                        <span className="mr-2 ml-2 text-danger">*</span>
                    )}
                {r.context.ctx.allowNewTab && (
                    <button
                        type="button"
                        className="btn bg-transparent p-0 ml-2"
                        tabIndex={-1}
                        onClick={(e) => {
                            e.stopPropagation();
                            g.context.ctx.refs.onNewTabClick(r);
                        }}
                    >
                        <div className="d-inline cmps-icon cmps-cdk-fill-grey-300 legacy:hover:cmps-cdk-fill-green-100">
                            <svg>
                                <path d={mdiOpenInNew}></path>
                            </svg>
                        </div>
                    </button>
                )}
                {(r.context.ctx.hasSuperGroup === false ||
                    r.context.ctx.hasQueue === false) && (
                    <>
                        <div ref={targetRef}>
                            <div
                                className="d-inline cmps-icon cmps-cdk-fill-red-100"
                                onMouseEnter={() => setShowTooltip(true)}
                                onMouseLeave={() => setShowTooltip(false)}
                            >
                                <svg>
                                    <path d={mdiExclamationThick}></path>
                                </svg>
                            </div>
                        </div>
                        {showTooltip && (
                            <Tooltip
                                targetRef={targetRef}
                                isVisible={showTooltip}
                                content={g.context.ctx.refs.translate.instant(
                                    r.context.ctx.hasSuperGroup === false
                                        ? TranslationKeyMap._11081_THIS_QUEUE_IS_NOT_PART_OF_THE_SUPERGROUP
                                        : TranslationKeyMap._11082_THIS_SUPERGROUP_IS_NOT_ASSOCIATED_TO_THE_QUEUE,
                                )}
                            />
                        )}
                    </>
                )}

                {/* eslint-disable-next-line @typescript-eslint/prefer-optional-chain */}
                {g.context.ctx &&
                    g.context.ctx.trendVisible &&
                    !ctx.value.unSelectable && (
                        <button
                            type="button"
                            className="btn bg-transparent p-0 ml-2"
                            tabIndex={-1}
                            onClick={(e) => {
                                e.stopPropagation();
                                g.context.ctx.refs.onToggleSelectionItemClick(
                                    r,
                                );
                            }}
                        >
                            <div
                                className={`d-inline cmps-icon ${classNames({
                                    'legacy:cmps-cdk-fill-green-100':
                                        r.context.ctx.selected,
                                    'cmps-cdk-fill-grey-300':
                                        !r.context.ctx.selected,
                                })}`}
                            >
                                <svg>
                                    <path d={mdiCheckBold}></path>
                                </svg>
                            </div>
                        </button>
                    )}
            </div>
        </div>
    );
}

export function MetricElement(
    props: Context<ColumnContext, CellContext<MetricPair>>,
): JSX.Element {
    const [renderTrigger, setRenderTrigger] = useState(false);
    (props as any).setRenderTrigger(renderTrigger, setRenderTrigger);

    const { g, c, ctx } = props;
    return (
        <div className="insight-node-metric">
            {c.ctx.type === ColumnType.CURRENT_ACTUAL && (
                <>
                    <span
                        className={classNames({
                            'insight-metric-bold':
                                g.context.ctx.showThresholdColours &&
                                ctx.value.metric.targetThresholdBold,
                        })}
                        style={{
                            color:
                                g.context.ctx.showThresholdColours &&
                                ctx.value.metric.targetThresholdColor,
                        }}
                    >
                        {ctx.value.metric.actual == null
                            ? '-'
                            : g.context.ctx.refs.cmpsNumber.transform(
                                  ctx.value.metric.actual,
                                  '1.2-2',
                              )}
                    </span>
                    {g.context.ctx.showThresholdColours && (
                        <span className="sr-only">
                            {ctx.value.metric.targetThresholdName}
                        </span>
                    )}
                    {g.context.ctx.showTrendArrows &&
                        ctx.value.metric.trendClass?.split(' ')?.length > 1 && (
                            <>
                                <div
                                    style={{
                                        color: ctx.value.metric.trendClass.split(
                                            ' ',
                                        )[0],
                                    }}
                                    className="d-inline cmps-icon"
                                >
                                    <svg>
                                        {ctx.value.metric.trendClass
                                            .split(' ')[1]
                                            .toLowerCase()
                                            .includes('up') && (
                                            <path d={mdiChevronUp}></path>
                                        )}
                                        {ctx.value.metric.trendClass
                                            .split(' ')[1]
                                            .toLowerCase()
                                            .includes('down') && (
                                            <path d={mdiChevronDown}></path>
                                        )}
                                    </svg>
                                </div>
                                <span className="sr-only">
                                    {ctx.value.metric.trendName}
                                </span>
                            </>
                        )}
                </>
            )}
            {c.ctx.type === ColumnType.PROJECTED_RESULT && (
                <span>
                    {ctx.value.metric.projectedActual == null
                        ? '-'
                        : g.context.ctx.refs.cmpsNumber.transform(
                              ctx.value.metric.projectedActual,
                              '1.2-2',
                          )}
                </span>
            )}
            {c.ctx.type === ColumnType.PERIOD_TARGET && (
                <span>
                    {ctx.value.metric.projectedTarget == null
                        ? '-'
                        : g.context.ctx.refs.cmpsNumber.transform(
                              ctx.value.metric.projectedTarget,
                              '1.2-2',
                          )}
                </span>
            )}
            {c.ctx.type === ColumnType.GAP && (
                <span>
                    {ctx.value.metric.gap == null
                        ? '-'
                        : g.context.ctx.refs.cmpsNumber.transform(
                              ctx.value.metric.gap,
                              '1.2-2',
                          )}
                </span>
            )}
            {c.ctx.type === ColumnType.PROJECTED_PERCENT_TO_TARGET && (
                <span>
                    {ctx.value.metric.projectedPctToTgt == null
                        ? '-'
                        : g.context.ctx.refs.cmpsPercent.transform(
                              ctx.value.metric.projectedPctToTgt,
                              '1.2-2',
                          )}
                </span>
            )}
            {c.ctx.type === ColumnType.PREVIOUS_ACTUAL && (
                <span>
                    {ctx.value.metric.previousActual == null
                        ? '-'
                        : g.context.ctx.refs.cmpsNumber.transform(
                              ctx.value.metric.previousActual,
                              '1.2-2',
                          )}
                </span>
            )}
            {c.ctx.type === ColumnType.CURRENT_TARGET && (
                <span>
                    {ctx.value.metric.target == null
                        ? '-'
                        : g.context.ctx.refs.cmpsNumber.transform(
                              ctx.value.metric.target,
                              '1.2-2',
                          )}
                </span>
            )}
            {c.ctx.type === ColumnType.GOAL && (
                <span>
                    {ctx.value.metric.goal == null
                        ? '-'
                        : g.context.ctx.refs.cmpsNumber.transform(
                              ctx.value.metric.goal,
                              '1.2-2',
                          )}
                </span>
            )}
            {c.ctx.type === ColumnType.PERCENT_TO_TARGET && (
                <span>
                    {ctx.value.metric.percentToTarget == null
                        ? '-'
                        : g.context.ctx.refs.cmpsPercent.transform(
                              ctx.value.metric.percentToTarget,
                              '1.2-2',
                          )}
                </span>
            )}
            {c.ctx.type === ColumnType.PEER_GROUP_ACTUAL && (
                <span>
                    {!ctx.value.peerGroup || ctx.value.peerGroup.actual == null
                        ? '-'
                        : g.context.ctx.refs.cmpsNumber.transform(
                              ctx.value.peerGroup.actual,
                              '1.2-2',
                          )}
                </span>
            )}
            {c.ctx.type === ColumnType.PEER_GROUP_PERCENT_TO_TARGET && (
                <span>
                    {!ctx.value.peerGroup ||
                    ctx.value.peerGroup.percentToTarget == null
                        ? '-'
                        : g.context.ctx.refs.cmpsPercent.transform(
                              ctx.value.peerGroup.percentToTarget,
                              '1.2-2',
                          )}
                </span>
            )}
            {c.ctx.type === ColumnType.OBJECTIVE_MET && (
                <span>
                    {ctx.value.metric.objectiveMet == null
                        ? '-'
                        : !ctx.value.metric.objectiveMet
                        ? 'N'
                        : 'Y'}
                </span>
            )}
            {c.ctx.type === ColumnType.WEIGHT && (
                <span>
                    {ctx.value.metric.weight == null
                        ? '-'
                        : g.context.ctx.refs.cmpsNumber.transform(
                              ctx.value.metric.weight,
                              '1.2-2',
                          )}
                </span>
            )}
            {c.ctx.type === ColumnType.WEIGHTED_SCORE && (
                <span>
                    {ctx.value.metric.weightedScore == null
                        ? '-'
                        : g.context.ctx.refs.cmpsNumber.transform(
                              ctx.value.metric.weightedScore,
                              '1.2-2',
                          )}
                </span>
            )}
        </div>
    );
}

export const render = (context: Context): RenderContext => {
    let tpl = context.ctx ? context.ctx.tpl : null;
    if (!tpl) {
        if (context.t === 'grid') {
            tpl = DefaultGrid;
        }
        if (context.t === 'row') {
            tpl = DefaultRow;
        }
        if (context.t === 'cell') {
            tpl = DefaultCell;
        }
    }
    if (!tpl) return;
    let _renderTrigger: boolean;
    let _setRenderTrigger: (value: boolean) => void;
    (context as any).setRenderTrigger = (renderTrigger, setRenderTrigger) => {
        _renderTrigger = renderTrigger;
        _setRenderTrigger = setRenderTrigger;
    };
    const el = React.createElement(tpl, context);
    const cont = document.createElement('div');
    const prom = new Promise<HTMLElement>((resolve) => {
        ReactDOM.render(el, cont, () => {
            resolve(cont.children[0] as HTMLElement);
        });
    });
    return {
        element: cont,
        // eslint-disable-next-line @typescript-eslint/promise-function-async
        innerElement: () => prom,
        detectChanges: (action) => {
            if (action !== 'layout' && action !== 'force') return;
            // ReactDOM.render(tpl(context), cont);
            _setRenderTrigger?.(!_renderTrigger);
        },
        destroy: () => {
            // parent element of grid is already in react so this is not needed?
            // ReactDOM.unmountComponentAtNode(cont);
        },
    };
};
